import AppPanel from '../fw/AppPanel.react';
import React from 'react';
import PropTypes from 'prop-types';
import Relay from 'react-relay';
import {injectIntl, defineMessages, FormattedMessage} from 'react-intl';
import {wrapNotFound} from '../app/NotFoundHandler.react';

import types from '../../common/model-api/types';
import * as Tool from '../fw/Tool';
import JobDetail from './JobDetail.react';

import './progress.scss';
import {safeJSONparse} from "../../common/util/safeJSONparse";
import {STATUS_FINISHED, STATUS_WAITING, STATUS_FAILED, STATUS_SKIPPED, STATUS_IN_PROGRESS} from '../../common/model-api/JobType';
import {getLastOrganisationId} from '../app/LastOrganisationId';

// eslint-disable-next-line react/prop-types
export function renderProgressBox({percent, maxValue, text, styles}) {
	maxValue = maxValue || 100;
	text = text || '' + percent + ' %';
	percent = percent || 1;
	styles = styles || {};

	return (
		<div className="progress job-progress">
			<div aria-valuemax={maxValue} aria-valuemin="0" aria-valuenow={percent}
				className="progress-bar progress-bar-striped active" role="progressbar"
				style={{
					width: (percent < 1 ? 1 : percent) + "%", //always show slim starting bar
					...styles
				}}>
			</div>
			<span className="textValue">{text}</span>
		</div>
	);
}

export function renderProgress(type, item, formatMessage) {
    let progress = item.progress;

    if (typeof progress === "string") {
        progress = safeJSONparse(progress);
    }

    if (!progress) {
        progress = {};
    }

	switch (item.status) {
		case STATUS_WAITING:
			return renderProgressBox({
				percent: 0,
				text: (<span className="waiting"><FormattedMessage defaultMessage="Waiting" id="jobProgress.waiting" /></span>),
			});
		case STATUS_FINISHED:
			return renderProgressBox({
				percent: 100,
				text: (<FormattedMessage defaultMessage="Finished" id="jobProgress.finished" />),
				styles: {
					animation: 'none',
					background: type.fields('status').values[STATUS_IN_PROGRESS].color
				}
			});
		case STATUS_FAILED:
			return renderProgressBox({
				percent: 100,
				text: (<FormattedMessage defaultMessage="Failed" id="jobProgress.failed" />),
				styles: {
					animation: 'none',
					background: type.fields('status').values[STATUS_FAILED].color
				}
			});
		case STATUS_SKIPPED:
			return renderProgressBox({
				percent: 100,
				text: (<FormattedMessage defaultMessage="Skipped" id="jobProgress.skipped" />),
				styles: {
					animation: 'none',
					background: type.fields('status').values[STATUS_SKIPPED].color
				}
			});
	}

    if (progress.running && item.status !== 'failed') {
        let percent = 0;
        if (progress.maxProgress) {
            percent = Math.round((progress.currentProgress / progress.maxProgress) * 100.0);
        } else {
            percent = progress.currentProgress;
        }

        let maxValue = progress.maxProgress;

        if (maxValue) {
            return (
                <div className="progress job-progress">
                    <div aria-valuemax={maxValue} aria-valuemin="0" aria-valuenow={percent}
                         className="progress-bar progress-bar-striped active" role="progressbar"
                         style={{width: percent + "%"}}>
                    </div>
					<span className="textValue">{percent} %</span>
                </div>
            );
        } else {
            return (
                <div className="progress job-progress">
                    <div aria-valuemax={100} aria-valuemin="0" aria-valuenow={100}
                         className="progress-bar progress-bar-striped active" role="progressbar"
                         style={{width: "100%"}}>
                    </div>
					<span className="textValue">{percent}</span>
                </div>
            );
        }
    } else {
        let value = Tool.getFormatted(type, 'status', formatMessage, item);
        let selectedValue = type.fields('status').values[item['status']];
        if (selectedValue) {
            return <span style={{color: selectedValue.color}}>{value}</span>;
        } else {
            return value;
        }
    }
}

const messages = defineMessages({
    title: {id: 'jobView.title', 'defaultMessage': 'Job View'}
});

class JobView extends React.Component {

    typeName = 'jobs';

    constructor(props) {
        super(props);
    }

    static propTypes = {
        intl: PropTypes.object.isRequired,
        relay: PropTypes.object.isRequired,
        user: PropTypes.object.isRequired,
    };

    render() {
        let { user } = this.props;
        let { job } = user.organisation;

        let type = types[this.typeName];

        return (
            <AppPanel breadcrumbParams={[
                {type, data: job},
                {type: types['organisations'], data: this.props.user.organisation}
            ]} title={this.props.intl.formatMessage(messages.title)}>
                <JobDetail job={job} />
            </AppPanel>
        );
    }
}

export default Relay.createContainer(wrapNotFound(injectIntl(JobView), (props) => {
    return props.user.organisation && props.user.organisation.job;
}), {
    initialVariables: {
        organisationId: '',
        jobId: ''
    },
    prepareVariables (variables) {
		if (!variables.organisationId) {
			variables.organisationId = getLastOrganisationId();
		}

        return variables;
    },
    fragments: {
        user: () => Relay.QL`
            fragment on User {
                organisation(id: $organisationId) {
                    id,
                    name,
                    job(id: $jobId) {
                        ${JobDetail.getFragment('job')}
                    }
                }
            }
        `,
    },
});
